/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  CancelBatchExecutionPathParameters,
  GetBatchExecutionsByDashboardPathParameters,
  TLBatchExecution,
  TLBatchExecutionsResponse,
  TLDeleteBatchExecutionsRequest,
  TLHTTPValidationError,
  TLSuccessResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Delete Batch Executions
 */
export const deleteBatchExecutions = (
  tLDeleteBatchExecutionsRequest: BodyType<TLDeleteBatchExecutionsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/v2/data-harmonization/batch-executions`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      data: tLDeleteBatchExecutionsRequest,
      signal,
    },
    options,
  );
};

export const getDeleteBatchExecutionsQueryKey = (
  tLDeleteBatchExecutionsRequest?: BodyType<TLDeleteBatchExecutionsRequest>,
) => {
  return ['DELETE', 'v2', 'data-harmonization', 'batch-executions', tLDeleteBatchExecutionsRequest] as const;
};

export const getDeleteBatchExecutionsQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteBatchExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteBatchExecutionsRequest: BodyType<TLDeleteBatchExecutionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteBatchExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteBatchExecutionsQueryKey(tLDeleteBatchExecutionsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteBatchExecutions>>> = ({ signal }) =>
    deleteBatchExecutions(tLDeleteBatchExecutionsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof deleteBatchExecutions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DeleteBatchExecutionsQueryResult = NonNullable<Awaited<ReturnType<typeof deleteBatchExecutions>>>;
export type DeleteBatchExecutionsQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteBatchExecutions<
  TData = Awaited<ReturnType<typeof deleteBatchExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteBatchExecutionsRequest: BodyType<TLDeleteBatchExecutionsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteBatchExecutions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteBatchExecutions>>,
          TError,
          Awaited<ReturnType<typeof deleteBatchExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteBatchExecutions<
  TData = Awaited<ReturnType<typeof deleteBatchExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteBatchExecutionsRequest: BodyType<TLDeleteBatchExecutionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteBatchExecutions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteBatchExecutions>>,
          TError,
          Awaited<ReturnType<typeof deleteBatchExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteBatchExecutions<
  TData = Awaited<ReturnType<typeof deleteBatchExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteBatchExecutionsRequest: BodyType<TLDeleteBatchExecutionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteBatchExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Batch Executions
 */

export function useDeleteBatchExecutions<
  TData = Awaited<ReturnType<typeof deleteBatchExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteBatchExecutionsRequest: BodyType<TLDeleteBatchExecutionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteBatchExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteBatchExecutionsQueryOptions(tLDeleteBatchExecutionsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Batch Executions
 */
export const prefetchDeleteBatchExecutionsQuery = async <
  TData = Awaited<ReturnType<typeof deleteBatchExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLDeleteBatchExecutionsRequest: BodyType<TLDeleteBatchExecutionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteBatchExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteBatchExecutionsQueryOptions(tLDeleteBatchExecutionsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Batch Executions
 */
export const invalidateDeleteBatchExecutions = async (
  queryClient: QueryClient,
  tLDeleteBatchExecutionsRequest: BodyType<TLDeleteBatchExecutionsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getDeleteBatchExecutionsQueryKey(tLDeleteBatchExecutionsRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Batch Executions By Dashboard
 */
export const getBatchExecutionsByDashboard = (
  { dashboardId }: GetBatchExecutionsByDashboardPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBatchExecutionsResponse>(
    { url: `/v2/data-harmonization/batch-executions/dashboards/${dashboardId}`, method: 'GET', signal },
    options,
  );
};

export const getGetBatchExecutionsByDashboardQueryKey = ({
  dashboardId,
}: GetBatchExecutionsByDashboardPathParameters) => {
  return ['v2', 'data-harmonization', 'batch-executions', 'dashboards', dashboardId] as const;
};

export const getGetBatchExecutionsByDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetBatchExecutionsByDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBatchExecutionsByDashboardQueryKey({ dashboardId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>> = ({ signal }) =>
    getBatchExecutionsByDashboard({ dashboardId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dashboardId !== null && dashboardId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetBatchExecutionsByDashboardQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>
>;
export type GetBatchExecutionsByDashboardQueryError = ErrorType<TLHTTPValidationError>;

export function useGetBatchExecutionsByDashboard<
  TData = Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetBatchExecutionsByDashboardPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>,
          TError,
          Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBatchExecutionsByDashboard<
  TData = Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetBatchExecutionsByDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>,
          TError,
          Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBatchExecutionsByDashboard<
  TData = Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetBatchExecutionsByDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Batch Executions By Dashboard
 */

export function useGetBatchExecutionsByDashboard<
  TData = Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dashboardId }: GetBatchExecutionsByDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetBatchExecutionsByDashboardQueryOptions({ dashboardId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Batch Executions By Dashboard
 */
export const prefetchGetBatchExecutionsByDashboardQuery = async <
  TData = Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dashboardId }: GetBatchExecutionsByDashboardPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBatchExecutionsByDashboard>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetBatchExecutionsByDashboardQueryOptions({ dashboardId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Batch Executions By Dashboard
 */
export const invalidateGetBatchExecutionsByDashboard = async (
  queryClient: QueryClient,
  { dashboardId }: GetBatchExecutionsByDashboardPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetBatchExecutionsByDashboardQueryKey({ dashboardId }) }, options);

  return queryClient;
};

/**
 * @summary Cancel Batch Execution
 */
export const cancelBatchExecution = (
  { batchExecutionId }: CancelBatchExecutionPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBatchExecution>(
    { url: `/v2/data-harmonization/batch-executions/${batchExecutionId}/cancel`, method: 'POST', signal },
    options,
  );
};

export const getCancelBatchExecutionQueryKey = ({ batchExecutionId }: CancelBatchExecutionPathParameters) => {
  return ['POST', 'v2', 'data-harmonization', 'batch-executions', batchExecutionId, 'cancel'] as const;
};

export const getCancelBatchExecutionQueryOptions = <
  TData = Awaited<ReturnType<typeof cancelBatchExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { batchExecutionId }: CancelBatchExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelBatchExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCancelBatchExecutionQueryKey({ batchExecutionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof cancelBatchExecution>>> = ({ signal }) =>
    cancelBatchExecution({ batchExecutionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: batchExecutionId !== null && batchExecutionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof cancelBatchExecution>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CancelBatchExecutionQueryResult = NonNullable<Awaited<ReturnType<typeof cancelBatchExecution>>>;
export type CancelBatchExecutionQueryError = ErrorType<TLHTTPValidationError>;

export function useCancelBatchExecution<
  TData = Awaited<ReturnType<typeof cancelBatchExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelBatchExecutionPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelBatchExecution>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelBatchExecution>>,
          TError,
          Awaited<ReturnType<typeof cancelBatchExecution>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelBatchExecution<
  TData = Awaited<ReturnType<typeof cancelBatchExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelBatchExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelBatchExecution>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelBatchExecution>>,
          TError,
          Awaited<ReturnType<typeof cancelBatchExecution>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelBatchExecution<
  TData = Awaited<ReturnType<typeof cancelBatchExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelBatchExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelBatchExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel Batch Execution
 */

export function useCancelBatchExecution<
  TData = Awaited<ReturnType<typeof cancelBatchExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { batchExecutionId }: CancelBatchExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelBatchExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCancelBatchExecutionQueryOptions({ batchExecutionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Cancel Batch Execution
 */
export const prefetchCancelBatchExecutionQuery = async <
  TData = Awaited<ReturnType<typeof cancelBatchExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { batchExecutionId }: CancelBatchExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelBatchExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCancelBatchExecutionQueryOptions({ batchExecutionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Cancel Batch Execution
 */
export const invalidateCancelBatchExecution = async (
  queryClient: QueryClient,
  { batchExecutionId }: CancelBatchExecutionPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCancelBatchExecutionQueryKey({ batchExecutionId }) }, options);

  return queryClient;
};
